/**
 * Shared permission catalog (mirrors PERMISSION_CATALOG in
 * backend/app/routes/user_category_routes.py). Order = display order in the
 * "Define User Category" tree.
 */
export const MODULES = [
  { key: 'brief',    label: 'Brief',    children: null },
  { key: 'pulse',    label: 'Pulse',    children: null },
  { key: 'warroom',  label: 'War Room', children: null },
  { key: 'synapse',  label: 'Synapse',  children: null },
  { key: 'review',   label: 'Review',   children: null },
  { key: 'settings', label: 'Settings', children: null,
    hint: 'Grants access to user & category management. Restricted by default.' },
  { key: 'spaceIntel', label: 'Space Intel', children: null },
  { key: 'salesAnalytics', label: 'Sales Analytics', children: [
    { key: 'departments',  label: 'Departments' },
    { key: 'festivals',    label: 'Festivals' },
    { key: 'peakHours',    label: 'Peak Hours' },
    { key: 'salesperson',  label: 'Salesperson' },
    { key: 'vendors',      label: 'Vendors' },
    { key: 'productIntel', label: 'Product Intel' },
    { key: 'returns',      label: 'Returns' },
  ] },
  { key: 'stockIntel', label: 'Stock Intelligence', children: [
    { key: 'stock',             label: 'Stock Intel' },
    { key: 'productDeepDive',   label: 'Product Dive' },
    { key: 'deadStockRecovery', label: 'Recovery' },
    { key: 'vendorScorecard',   label: 'Vendor Score' },
  ] },
  { key: 'procurement', label: 'Procurement', children: [
    { key: 'purchaseIntel',  label: 'Purchase Intel' },
    { key: 'vendor360',      label: 'Vendor 360' },
    { key: 'festivalBuying', label: 'Festival Buying' },
    { key: 'logistics',      label: 'Logistics' },
  ] },
  { key: 'peopleIntel', label: 'People Intel', children: [
    { key: 'hrOverview',   label: 'HR Overview' },
    { key: 'attendance',   label: 'Attendance' },
    { key: 'attrition',    label: 'Attrition' },
    { key: 'performance',  label: 'Performance' },
    { key: 'workforce',    label: 'Workforce' },
    { key: 'demographics', label: 'Demographics' },
    { key: 'interns',      label: 'Interns' },
    { key: 'employeeDna',  label: 'Employee DNA' },
  ] },
  { key: 'market', label: 'Market', children: [
    { key: 'marketIntel', label: 'Market Intel' },
  ] },
];

// Path → permission key mapping (used by Layout to filter the sidebar)
export const PATH_TO_PERM = {
  '/brief':    'brief',
  '/pulse':    'pulse',
  '/warroom':  'warroom',
  '/ask':      'synapse',
  '/review':   'review',
  '/settings': 'settings',
  '/space-intel': 'spaceIntel',
  // Sales Analytics
  '/analysis/departments':   'salesAnalytics.departments',
  '/analysis/festivals':     'salesAnalytics.festivals',
  '/analysis/peak-hours':    'salesAnalytics.peakHours',
  '/analysis/salesperson':   'salesAnalytics.salesperson',
  '/analysis/vendors':       'salesAnalytics.vendors',
  '/analysis/product-intel': 'salesAnalytics.productIntel',
  '/analysis/returns':       'salesAnalytics.returns',
  // Stock Intelligence
  '/analysis/stock':               'stockIntel.stock',
  '/analysis/item-run-rate':       'stockIntel.stock',
  '/analysis/product-deep-dive':   'stockIntel.productDeepDive',
  '/analysis/dead-stock-recovery': 'stockIntel.deadStockRecovery',
  '/analysis/vendor-scorecard':    'stockIntel.vendorScorecard',
  // Procurement
  '/procurement':                    'procurement.purchaseIntel',
  '/procurement/vendor-360':         'procurement.vendor360',
  '/procurement/festival-buying':    'procurement.festivalBuying',
  '/procurement/logistics':          'procurement.logistics',
  // People Intel
  '/hr/overview':       'peopleIntel.hrOverview',
  '/hr/attendance':     'peopleIntel.attendance',
  '/hr/attrition':      'peopleIntel.attrition',
  '/hr/performance':    'peopleIntel.performance',
  '/hr/workforce':      'peopleIntel.workforce',
  '/hr/demographics':   'peopleIntel.demographics',
  '/hr/interns':        'peopleIntel.interns',
  '/hr/ideal-employee': 'peopleIntel.employeeDna',
  // Market
  '/analysis/market-intel': 'market.marketIntel',
};

// Group-key mapping for parent modules (e.g. all /analysis/* except market)
export const GROUP_KEY = {
  salesAnalytics: 'salesAnalytics',
  stockIntel:     'stockIntel',
  procurement:    'procurement',
  peopleIntel:    'peopleIntel',
  market:         'market',
};

// Utility — tri-state for a parent: 'all' | 'some' | 'none'
export const parentState = (permSet, parent) => {
  const kids = (MODULES.find(m => m.key === parent)?.children || []).map(c => `${parent}.${c.key}`);
  if (kids.length === 0) return permSet.has(parent) ? 'all' : 'none';
  const on = kids.filter(k => permSet.has(k)).length;
  if (on === 0) return 'none';
  if (on === kids.length) return 'all';
  return 'some';
};

// Utility — check if a user can visit a path given their permission array
export const canAccessPath = (permissions, path) => {
  if (!path) return true;
  const key = PATH_TO_PERM[path];
  if (!key) return true;                     // paths without an entry are always visible
  return permissions.includes(key);
};
